import { createSlice } from '@reduxjs/toolkit';
import { clearState } from '../../rootAccess';

// ============================================================================
// HELPERS
// ============================================================================

const findChatIndex = (chats, chatId) => {
   return chats.findIndex(chat => chat.fromUserId === chatId);
};

// ============================================================================
// SLICE
// ============================================================================

const initialState = {
   chats: [],
   loading: false,
   error: null,
   hasMore: true,
   searchText: '',
   searchTextByScope: {},
   unreadRecords: 0,
   archivedChatIds: [],
};

const recentChatListSlice = createSlice({
   name: 'recentChatData',
   initialState,
   reducers: {
      appendRecentChats: (state, action) => {
         const incomingChats = action.payload;
         if (!incomingChats?.length) {
            state.hasMore = false;
            return;
         }

         const existingIds = new Set(state.chats.map(c => c.fromUserId));
         const newChats = incomingChats.filter(c => !existingIds.has(c.fromUserId));

         if (newChats.length > 0) {
            state.chats.push(...newChats);
         }
      },

      setRecentChats: (state, action) => {
         const chats = action.payload || [];
         const sortedChats = [...chats];
         state.chats = sortedChats;
         state.hasMore = true;
         state.loading = false;
      },

      setArchivedChatIds: (state, action) => {
         const newArchivedIds = action.payload || [];
         state.archivedChatIds = newArchivedIds;

         // Sync all existing chats with the new archive list
         state.chats.forEach(chat => {
            const isNowArchived = newArchivedIds.includes(chat.fromUserId);
            chat.archiveStatus = isNowArchived ? 1 : 0;
         });
      },

      setRecentChatHasMore: (state, action) => {
         state.hasMore = action.payload;
      },

      upsertRecentChatItem: (state, action) => {
         const chat = action.payload;
         const { fromUserId, msgId, timestamp } = chat;

         const index = findChatIndex(state.chats, fromUserId);
         if (index !== -1) {
            const existing = state.chats[index];

            // 1. Precise Match: If update is for the SAME message ID
            if (existing.msgId === msgId) {
               // Merge bodies to preserve fields like meet title/link if SDK callback is sparse
               state.chats[index] = {
                  ...existing,
                  ...chat,
                  msgBody: { ...existing.msgBody, ...chat.msgBody },
                  // If we already detected it's a meet, don't let SDK revert it to 'text'
                  msgType: existing.msgType === 'meet' ? 'meet' : chat.msgType || existing.msgType,
               };
               return;
            }

            // 2. Chronology Guard: Ignore updates about older messages clobbering the top.
            // Skipped for the local user's own just-sent message — that's generated from
            // timeSync's client clock, which can trail a previously *received* message's
            // server-assigned timestamp by a little (clock-sync drift). Without this
            // exception, sending a reply right after receiving a message could get
            // silently dropped here, leaving the old incoming message stuck as the
            // recent-chats preview even though the user just replied.
            if (!chat.isLastMessageSentByMe && timestamp && existing.timestamp && timestamp < existing.timestamp) {
               return;
            }

            // 3. Normal Update: Move to top
            state.chats[index] = {
               ...existing,
               ...chat,
               msgBody: { ...existing.msgBody, ...chat.msgBody },
            };
            const item = state.chats.splice(index, 1)[0];
            state.chats.unshift(item);
         } else {
            state.chats.unshift(chat);
         }
      },

      upsertMeetRecentChats: (state, action) => {
         const incomingChats = action.payload || [];
         if (!incomingChats.length) return;

         // incomingChats is in send order (oldest → newest). Unshifting in that same
         // order means each later (newer) chat overtakes the earlier ones, so the
         // most-recently-sent chat naturally lands on top — matching how a refresh
         // re-sorts by actual timestamp. Reversing here previously put the oldest
         // recipient's chat on top instead, which only became visible after a refresh
         // corrected it back to true chronological order.
         incomingChats.forEach(chat => {
            const { fromUserId } = chat;
            const index = findChatIndex(state.chats, fromUserId);
            if (index !== -1) {
               const existing = state.chats[index];
               // Robust merge
               state.chats[index] = {
                  ...existing,
                  ...chat,
                  msgBody: { ...existing.msgBody, ...chat.msgBody },
               };
               const item = state.chats.splice(index, 1)[0];
               state.chats.unshift(item);
            } else {
               state.chats.unshift(chat);
            }
         });
      },

      setRecentChatsLoading: (state, action) => {
         state.loading = action.payload;
      },

      setSearchText: (state, action) => {
         state.searchText = action.payload;
      },

      setSearchTextByScope: (state, action) => {
         const { scope, text } = action.payload;
         state.searchTextByScope[scope] = text;
      },

      clearRecentChat: (state, action) => {
         const { fromUserId } = action.payload;
         const index = findChatIndex(state.chats, fromUserId);
         if (index !== -1) {
            state.chats[index] = {
               ...state.chats[index],
               msgBody: {},
               msgType: '',
               unreadCount: 0,
               unreadMessageId: '',
            };
         }
      },

      updateRecentChatStatus: (state, action) => {
         const { chatId, msgId, msgStatus } = action.payload;
         const index = findChatIndex(state.chats, chatId);
         if (index !== -1 && state.chats[index].msgId === msgId) {
            state.chats[index].msgStatus = msgStatus;
         }
      },

      deleteRecentChat: (state, action) => {
         const { fromUserId } = action.payload;
         state.chats = state.chats.filter(c => c.fromUserId !== fromUserId);
      },

      updateArchiveStatus: (state, action) => {
         const { fromUserId, isArchived } = action.payload;

         // Update the master ID list
         if (isArchived) {
            if (!state.archivedChatIds.includes(fromUserId)) {
               state.archivedChatIds.push(fromUserId);
            }
         } else {
            state.archivedChatIds = state.archivedChatIds.filter(id => id !== fromUserId);
         }

         // Update the individual chat object if it exists in the list
         const index = findChatIndex(state.chats, fromUserId);
         if (index !== -1) {
            state.chats[index].archiveStatus = isArchived ? 1 : 0;
         }
      },

      decrementUnreadCount: (state, action) => {
         const { fromUserId, unreadMessageId } = action.payload;
         const index = findChatIndex(state.chats, fromUserId);
         if (index !== -1) {
            const chat = state.chats[index];
            if (chat.unreadCount > 0) {
               chat.unreadCount -= 1;
            }
            chat.unreadMessageId = unreadMessageId;
         }
      },

      updateRecentChatMessage: (state, action) => {
         const { chatId, msgId, msgBody, editMessageId, editedStatus } = action.payload;
         const index = findChatIndex(state.chats, chatId);
         if (index !== -1 && state.chats[index].msgId === msgId) {
            state.chats[index] = {
               ...state.chats[index],
               msgBody: { ...state.chats[index].msgBody, ...msgBody },
               editMessageId,
               editedStatus: editedStatus ?? state.chats[index].editedStatus,
            };
         }
      },

      recallMessageInRecentChat: (state, action) => {
         const { chatId, msgIds } = action.payload;
         const index = findChatIndex(state.chats, chatId);
         if (index !== -1 && msgIds.includes(state.chats[index].msgId)) {
            state.chats[index].deleteStatus = 1;
         }
      },

      repositionRecentChatAfterDelete: (state, action) => {
         const { chatId, formatted } = action.payload;
         const index = findChatIndex(state.chats, chatId);

         if (index === -1) return;

         // Capture existing data, merge with new formatted message, then remove from list
         const existingChat = state.chats[index];
         const updatedChat = { ...existingChat, ...formatted };
         state.chats.splice(index, 1);

         // Edge case: list is now empty
         const lastChat = state.chats[state.chats.length - 1];
         if (!lastChat) {
            state.chats.push(updatedChat);
            return;
         }

         const newMessageTime = formatted.messageTime;
         const lastChatTime = lastChat.messageTime;

         if (newMessageTime >= lastChatTime) {
            // Chat still belongs somewhere in the visible list — find the correct sorted position
            // List is newest-first, so find first chat whose time is <= newMessageTime
            const insertIndex = state.chats.findIndex(c => c.messageTime <= newMessageTime);
            if (insertIndex === -1) {
               state.chats.push(updatedChat); // Older than everything, goes to end
            } else {
               state.chats.splice(insertIndex, 0, updatedChat);
            }
         } else if (!state.hasMore) {
            // No more pages — safe to place it at the very end
            state.chats.push(updatedChat);
            // hasMore === true → just leave it removed; next paginated API call will restore it
         }
      },
   },
   extraReducers: builder => {
      builder.addCase(clearState, () => initialState);
   },
});

export const {
   setRecentChats,
   appendRecentChats,
   setRecentChatHasMore,
   upsertRecentChatItem,
   upsertMeetRecentChats,
   setRecentChatsLoading,
   setSearchText,
   setSearchTextByScope,
   clearRecentChat,
   deleteRecentChat,
   setArchivedChatIds,
   updateArchiveStatus,
   decrementUnreadCount,
   updateRecentChatStatus,
   updateRecentChatMessage,
   recallMessageInRecentChat,
   repositionRecentChatAfterDelete,
} = recentChatListSlice.actions;

export const { reducer: recentChatReducer } = recentChatListSlice;
